/* La fiche d'une vente : son parcours, ses lignes, la facture reçue de l'usine
   et la commission qui en découle. C'est le pivot — tout le reste s'y rattache.

   Les montants de commission affichés sont ceux que la base porte : la fiche
   écrit la vente, le moteur recalcule, et l'écran relit. Jamais l'inverse. */

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useAgents, useClients, useCommissionVente, useModeles, useParts, usePrixAgent, useProduits,
  useRegles, useReglesSurplus, useUsines, useVente, lireParametre,
} from "@/lib/donnees";
import { autorise, useNiveau } from "@/lib/session";
import { Banner, Empty, Section, Stepper, Tag } from "@/ui/primitives";
import { Champ, Drawer } from "@/ui/Drawer";
import { Document } from "@/ui/Document";
import { Icon } from "@/ui/Icon";
import { Table } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { aujourdhui, dateFr, entier, euro, poids } from "@/lib/format";
import { figee, voitTotal } from "@/domain/commissions";
import { planchersDe, sousPlancher } from "@/domain/prix";
import { ETAPES, blocage, etapeDe, etapeSuivante } from "@/domain/ventes";
import { MODE_LONG, NOM_PART, PAS, valeurBareme } from "@/features/commissions/libelles";
import {
  avancerVente, detacherFactureUsine, enregistrerFactureUsine, lienPiece,
  reglerCommissionVente,
} from "./actions";
import type { ContexteCommission, SaisieFacture } from "./actions";
import { Jalons } from "./Jalons";
import { Journal } from "@/features/journal/Journal";
import type {
  Commission, EtatCommission, LigneVente, ModeleDocument, ModePart, ModeRegle, Transparence,
  Usine, Vente,
} from "@/types/metier";

const ETATS: Record<EtatCommission, { nom: string; ton: string }> = {
  brouillon: { nom: "Brouillon", ton: "" },
  confirmee: { nom: "Confirmée", ton: "info" },
  a_facturer: { nom: "À facturer", ton: "accent" },
  facturee: { nom: "Facturée", ton: "warn" },
  payee: { nom: "Payée", ton: "ok" },
};

const VIDE: SaisieFacture = { numero: "", date: "", montant: "", fichier: null };

export function FicheVente() {
  const { id } = useParams<{ id: string }>();
  const navigue = useNavigate();
  const toast = useToast();
  const niveau = useNiveau();

  const { donnees: vente, chargement, erreur, recharger } = useVente(id);
  const { donnees: commission, recharger: rechargerComm } = useCommissionVente(id);
  const { donnees: regles } = useRegles();
  const { donnees: parts } = useParts();
  const { donnees: reglesSurplus } = useReglesSurplus();
  const { donnees: produits } = useProduits();
  const { donnees: prixAgent } = usePrixAgent();
  const { donnees: usines } = useUsines();
  const { donnees: clients } = useClients();
  const { donnees: agents } = useAgents();
  const { donnees: modeles } = useModeles();

  const [apercu, setApercu] = useState<"client" | "usine" | null>(null);
  const [transparence, setTransparence] = useState<Transparence>("niveau");
  const [saisie, setSaisie] = useState<SaisieFacture>(VIDE);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    void lireParametre<Transparence>("transparence", "niveau").then(setTransparence);
  }, []);

  useEffect(() => {
    setSaisie({ ...VIDE, date: vente?.date_document ?? aujourdhui() });
  }, [vente?.id, vente?.date_document]);

  const lignes = useMemo<LigneVente[]>(
    () => [...(vente?.lignes ?? [])].sort((a, b) => a.position - b.position),
    [vente],
  );

  /* Les planchers de l'agent qui porte CETTE vente, pas de celui qui regarde :
     c'est son tarif à lui que la vente doit respecter. */
  const planchers = useMemo(
    () => planchersDe(produits, prixAgent, vente?.agent_id ?? ""),
    [produits, prixAgent, vente?.agent_id],
  );
  const usine = usines.find((u) => u.id === vente?.usine_id) ?? null;
  const client = clients.find((c) => c.id === vente?.client_id) ?? null;
  const agent = agents.find((a) => a.id === vente?.agent_id) ?? null;

  if (erreur) return <div className="pad"><div className="banner">{erreur}</div></div>;
  if (chargement) return <div className="pad"><span className="chargement">Chargement…</span></div>;
  if (!vente) {
    return (
      <div className="pad">
        <Empty titre="Vente introuvable" detail="Ce document n'existe pas, ou ne vous est pas ouvert." />
      </div>
    );
  }

  const voitUsine = autorise(niveau, "agent");
  const tout = voitTotal(niveau, transparence);
  const c: Commission | null = commission;
  const m = vente.comm ?? {};
  /* Le barème d'une vente se règle comme un prix — tant qu'elle n'est pas facturée. */
  const reglable = autorise(niveau, "admin") && Boolean(c) && !(c && figee(c));
  const suivante = etapeSuivante(vente.etape);
  const empeche = suivante ? blocage(vente) : null;
  const facture = vente.facture_usine;
  const ctx: ContexteCommission = {
    regles, parts, commission: c, surplus: reglesSurplus, planchers,
  };

  /* Ce qui part sous le plancher de l'agent : la fiche le dit, elle ne
     l'empêche pas. Une vente au rabais peut être une décision. */
  const basses = sousPlancher(vente.lignes ?? [], planchers);

  async function agir(quoi: () => Promise<string>) {
    if (occupe) return;
    setOccupe(true);
    try {
      toast(await quoi());
      recharger();
      rechargerComm();
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'enregistrement a échoué.");
    } finally {
      setOccupe(false);
    }
  }

  const masqueUsine = (
    <span className="locked">
      <Icon n="cadenas" cls="" />
      Niveau Senior
    </span>
  );

  async function ouvrirPiece(chemin: string) {
    const url = await lienPiece(chemin);
    if (url) window.open(url, "_blank", "noopener");
    else toast("Le fichier n'a pas pu être ouvert.");
  }

  return (
    <div className="pad">
      <div className="card card-pad">
        <div className="fiche-h">
          <div className="fiche-t">
            <div className="section-t">{vente.reference}</div>
            <div className="section-n">
              {client?.nom ?? "—"}
              {vente.contact ? ` — ${vente.contact}` : ""}
            </div>
          </div>
          <div className="fiche-a">
            <button type="button" className="btn" onClick={() => navigue("/ventes")}>
              <Icon n="retour" cls="" />
              Toutes les ventes
            </button>
            <button type="button" className="btn" onClick={() => setApercu("client")}>
              <Icon n="listes" cls="" />
              Document client
            </button>
            {voitUsine ? (
              <button type="button" className="btn" onClick={() => setApercu("usine")}>
                <Icon n="usines" cls="" />
                Document usine
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              disabled={!suivante || Boolean(empeche) || occupe}
              title={empeche ?? undefined}
              onClick={() =>
                void agir(async () => {
                  const e = await avancerVente(vente, ctx);
                  return `${vente.reference} passe en ${etapeDe(e).nom.toLowerCase()}.`;
                })
              }
            >
              {suivante ? `Passer en ${etapeDe(suivante).nom.toLowerCase()}` : "Payé"}
            </button>
          </div>
        </div>

        <div className="fiche-parcours">
          <Stepper
            etapes={ETAPES.map((e) => e.court)}
            tons={ETAPES.map((e) => e.ton)}
            courante={etapeDe(vente.etape).court}
          />
        </div>

        {empeche ? <Banner>{empeche}</Banner> : null}
      </div>

      <Section titre="Récapitulatif">
        <div className="card card-pad">
          <dl className="kv">
            <dt>Client</dt>
            <dd>{client?.nom ?? "—"}</dd>
            <dt>Contact</dt>
            <dd>{vente.contact || "—"}</dd>
            <dt>Agent</dt>
            <dd>{agent?.nom ?? "—"}</dd>
            <dt>Usine</dt>
            <dd>{voitUsine ? (usine?.nom ?? "—") : masqueUsine}</dd>
            <dt>Date</dt>
            <dd className="mono">{dateFr(vente.date_document)}</dd>
            <dt>Montant HT</dt>
            <dd className="mono">{euro(Number(vente.montant))}</dd>
          </dl>
        </div>
      </Section>

      <Section titre="Lignes" note={`${lignes.length} ${lignes.length > 1 ? "lignes" : "ligne"}`}>
        {basses.length ? (
          <Banner>
            {basses.length === 1 ? "Une ligne part" : `${basses.length} lignes partent`} sous le
            prix mini de l'agent :{" "}
            {basses.slice(0, 4).map((b, i) => (
              <span key={b.ref}>
                {i > 0 ? ", " : ""}
                <b className="mono">{b.ref}</b> à {euro(b.prix)} au lieu de {euro(b.plancher)}
              </span>
            ))}
            {basses.length > 4 ? `, et ${basses.length - 4} autre(s)` : ""}.
          </Banner>
        ) : null}
        <Table
          lignes={lignes}
          cle={(l) => l.id}
          colonnes={[
            { cle: "ref", titre: "Référence", type: "ref", filtre: "texte", sous: (l) => l.nom },
            { cle: "nom", titre: "Désignation", classe: "name", mobile: false, filtre: "texte" },
            { cle: "conditionnement", titre: "Cond.", type: "ref", mobile: false },
            /* La quantité reste en unités — c'est elle qui fait le montant — mais
               la ligne rappelle d'où elle vient : « 36 » ne se vérifie pas,
               « 3 cartons de 12 » se vérifie d'un coup d'œil sur la palette. */
            {
              cle: "qte", titre: "Quantité", type: "num", total: true,
              nombre: (l) => Number(l.qte),
              sous: (l) =>
                l.cartons != null && l.par_carton
                  ? `${entier(Number(l.cartons))} × ${l.par_carton}`
                  : undefined,
            },
            {
              cle: "prix", titre: "Prix unit.", type: "euro", total: true, mobile: false,
              agregat: "moyenne", totalTitre: "Prix moyen", nombre: (l) => Number(l.prix),
            },
            {
              cle: "total", titre: "Montant", type: "euro", total: true,
              totalTitre: "Total HT", nombre: (l) => Number(l.total),
            },
          ]}
        />
      </Section>

      <Jalons
        vente={vente}
        ctx={ctx}
        modifiable={!c || !figee(c)}
        onFait={() => {
          recharger();
          rechargerComm();
        }}
      />

      <Section titre="Facture usine" note={facture ? "reçue et intégrée" : "à recevoir de l'usine"}>
        <div className="card card-pad">
          {facture ? (
            <>
              <dl className="kv">
                <dt>Numéro</dt>
                <dd className="mono">{facture.numero}</dd>
                <dt>Date</dt>
                <dd className="mono">{dateFr(facture.date)}</dd>
                <dt>Montant</dt>
                <dd className="mono">{euro(Number(facture.montant))}</dd>
                <dt>Émetteur</dt>
                <dd>{voitUsine ? (usine?.nom ?? "—") : masqueUsine}</dd>
                <dt>Fichier</dt>
                <dd>
                  {facture.fichier ? (
                    <button
                      type="button"
                      className="piece piece-btn"
                      disabled={!facture.fichier.chemin}
                      onClick={() => {
                        const ch = facture.fichier?.chemin;
                        if (ch) void ouvrirPiece(ch);
                      }}
                    >
                      <Icon n="listes" cls="" />
                      <span className="mono">{facture.fichier.nom}</span>
                      <span className="po">{poids(facture.fichier.taille)}</span>
                    </button>
                  ) : (
                    <span className="locked">aucun fichier joint</span>
                  )}
                </dd>
              </dl>
              <div className="fiche-actions">
                <button
                  type="button"
                  className="btn btn-sm"
                  disabled={occupe}
                  onClick={() =>
                    void agir(async () => {
                      await detacherFactureUsine(vente, ctx);
                      return `Facture usine détachée de ${vente.reference}.`;
                    })
                  }
                >
                  Remplacer la facture
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="champ-duo">
                <Champ id="f-num" label="Numéro de facture usine">
                  <input
                    id="f-num"
                    type="text"
                    value={saisie.numero}
                    placeholder={`${usine?.prefixe_facture ?? ""}0000`}
                    onChange={(e) => setSaisie({ ...saisie, numero: e.target.value })}
                  />
                </Champ>
                <Champ id="f-date" label="Date de la facture">
                  <input
                    id="f-date"
                    type="date"
                    value={saisie.date}
                    onChange={(e) => setSaisie({ ...saisie, date: e.target.value })}
                  />
                </Champ>
              </div>
              <div className="champ-duo">
                <Champ id="f-montant" label="Montant HT">
                  <input
                    id="f-montant"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={String(vente.montant)}
                    value={saisie.montant}
                    onChange={(e) => setSaisie({ ...saisie, montant: e.target.value })}
                  />
                </Champ>
                <Champ id="f-fichier" label="Fichier (PDF ou image)">
                  <input
                    id="f-fichier"
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) =>
                      setSaisie({ ...saisie, fichier: e.target.files?.[0] ?? null })
                    }
                  />
                </Champ>
              </div>
              {saisie.fichier ? (
                <div className="piece">
                  <Icon n="listes" cls="" />
                  <span className="mono">{saisie.fichier.name}</span>
                  <span className="po">{poids(saisie.fichier.size)}</span>
                </div>
              ) : null}
              <button
                type="button"
                className="btn btn-primary"
                disabled={occupe}
                onClick={() =>
                  void agir(async () => {
                    const f = await enregistrerFactureUsine(vente, saisie, ctx);
                    return `Facture usine ${f.numero} intégrée à ${vente.reference}${
                      c ? ". La commission porte désormais ce numéro." : "."
                    }`;
                  })
                }
              >
                Intégrer la facture
              </button>
            </>
          )}
        </div>
      </Section>

      <Section
        titre="Commission"
        note={c?.manuel ? "barème fixé sur cette vente" : "barème usine"}
      >
        <div className="card card-pad">
          {c ? (
            <>
              <div className="fiche-h">
                <div className="fiche-t">
                  <div className="row-t">
                    {c.mode === "mixte"
                      ? "Barème mixte"
                      : MODE_LONG[c.mode as ModeRegle]}
                  </div>
                  <div className="row-d">
                    {c.reference} · base {euro(Number(c.base))} · {entier(Number(c.quantite))} unités
                  </div>
                </div>
                <div className="fiche-a fiche-a-centre">
                  <span className="mono fiche-montant">
                    {euro(Number(tout ? c.montant : c.agent_montant))}
                  </span>
                  <Tag ton={ETATS[c.etat].ton} dot>
                    {ETATS[c.etat].nom}
                  </Tag>
                </div>
              </div>

              {tout ? (
                <dl className="kv kv-haut">
                  <dt>Barème</dt>
                  <dd>
                    {reglable ? (
                      <span className="bareme">
                        <select
                          aria-label="Mode de commission de cette vente"
                          value={m.mode ?? ""}
                          disabled={occupe}
                          onChange={(e) => {
                            const val = e.target.value as ModeRegle | "";
                            void agir(async () => {
                              await reglerCommissionVente(
                                vente,
                                val
                                  ? {
                                      ...m,
                                      mode: val,
                                      valeur: m.valeur ?? (val === "pct" ? Number(c.taux) : 0),
                                    }
                                  : { ...m, mode: null, valeur: null },
                                ctx,
                              );
                              return val
                                ? `Barème fixé sur ${vente.reference}.`
                                : `${vente.reference} repasse sous le barème usine.`;
                            });
                          }}
                        >
                          <option value="">Suivre le barème usine</option>
                          <option value="pct">{MODE_LONG.pct}</option>
                          <option value="unite">{MODE_LONG.unite}</option>
                        </select>
                        {m.mode ? (
                          <input
                            type="number"
                            min="0"
                            step={PAS[m.mode]}
                            aria-label="Valeur du barème de cette vente"
                            defaultValue={String(m.valeur ?? 0)}
                            disabled={occupe}
                            onBlur={(e) => {
                              const v = Math.max(0, parseFloat(e.target.value.replace(",", ".")) || 0);
                              if (v === Number(m.valeur ?? 0)) return;
                              void agir(async () => {
                                await reglerCommissionVente(vente, { ...m, valeur: v }, ctx);
                                return `Barème de ${vente.reference} : ${valeurBareme(m.mode as ModeRegle, v)}.`;
                              });
                            }}
                          />
                        ) : null}
                      </span>
                    ) : (
                      <span className="mono">
                        {c.manuel && m.mode
                          ? `${valeurBareme(m.mode, Number(m.valeur ?? 0))} — fixé sur cette vente`
                          : c.mode === "mixte"
                            ? "Barème mixte"
                            : MODE_LONG[c.mode as ModeRegle]}
                      </span>
                    )}
                  </dd>

                  <dt>Commission totale</dt>
                  <dd className="mono">
                    <b>{euro(Number(c.montant))}</b>
                    <span className="po">
                      {Number(c.taux).toFixed(2).replace(".", ",")} % de la base
                    </span>
                  </dd>

                  <dt>Part de l'agent</dt>
                  <dd>
                    {reglable ? (
                      <span className="bareme">
                        <select
                          aria-label="Mode de part agent sur cette vente"
                          value={m.partMode ?? ""}
                          disabled={occupe}
                          onChange={(e) => {
                            const val = e.target.value as ModePart | "";
                            void agir(async () => {
                              await reglerCommissionVente(
                                vente,
                                {
                                  ...m,
                                  partMode: val || null,
                                  partValeur: val ? (m.partValeur ?? Number(c.part_valeur ?? 0)) : null,
                                },
                                ctx,
                              );
                              return val
                                ? `Part agent fixée sur ${vente.reference}.`
                                : `La part agent suit à nouveau la règle de l'agent.`;
                            });
                          }}
                        >
                          <option value="">Suivre la règle de l'agent</option>
                          <option value="points">{NOM_PART.points}</option>
                          <option value="pct">{NOM_PART.pct}</option>
                          <option value="unite">{NOM_PART.unite}</option>
                        </select>
                        {m.partMode ? (
                          <input
                            type="number"
                            min="0"
                            step={PAS[m.partMode]}
                            aria-label="Valeur de la part agent"
                            defaultValue={String(m.partValeur ?? 0)}
                            disabled={occupe}
                            onBlur={(e) => {
                              const v = Math.max(0, parseFloat(e.target.value.replace(",", ".")) || 0);
                              if (v === Number(m.partValeur ?? 0)) return;
                              void agir(async () => {
                                await reglerCommissionVente(vente, { ...m, partValeur: v }, ctx);
                                return `Part agent : ${valeurBareme(m.partMode as ModePart, v)}.`;
                              });
                            }}
                          />
                        ) : null}
                        <span className="po">
                          {agent?.nom ?? "Agent"} — {euro(Number(c.agent_montant))}
                        </span>
                      </span>
                    ) : (
                      <span className="mono">
                        {euro(Number(c.agent_montant))}
                        {c.part_mode ? <span className="po">{NOM_PART[c.part_mode]}</span> : null}
                      </span>
                    )}
                  </dd>

                  <dt>Reste à Omnix</dt>
                  <dd className="mono">
                    <b>{euro(Number(c.omnix_montant))}</b>
                  </dd>
                </dl>
              ) : (
                <dl className="kv kv-haut">
                  <dt>Votre commission</dt>
                  <dd className="mono">
                    <b>{euro(Number(c.agent_montant))}</b>
                  </dd>
                  <dt>Mode</dt>
                  <dd>{c.part_mode ? NOM_PART[c.part_mode] : "—"}</dd>
                </dl>
              )}

              <div className="fiche-actions">
                <button type="button" className="btn btn-sm" onClick={() => navigue("/commissions")}>
                  Voir dans les commissions
                </button>
                {reglable && c.manuel ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    disabled={occupe}
                    onClick={() =>
                      void agir(async () => {
                        await reglerCommissionVente(vente, null, ctx);
                        return `${vente.reference} repasse sous le barème usine.`;
                      })
                    }
                  >
                    Revenir au barème usine
                  </button>
                ) : null}
              </div>

              {figee(c) ? (
                <Banner>
                  Commission facturée : son montant est figé et ne suit plus le barème.
                </Banner>
              ) : null}
            </>
          ) : (
            <div className="vide-ligne">
              Aucune commission : elle est créée au passage en bon de commande.
            </div>
          )}
        </div>
      </Section>

      <Journal
        sujet="ventes"
        sujetId={vente.id}
        titre="Suivi de la vente"
        note="relances, promesses, et ce qui a changé"
      />

      {apercu ? (
        <ApercuDocument
          cible={apercu}
          vente={vente}
          lignes={lignes}
          modeles={modeles}
          usine={usine}
          destinataire={
            apercu === "client"
              ? `${client?.nom ?? "Client"}${vente.contact ? ` — ${vente.contact}` : ""}`
              : (usine?.nom ?? "Usine")
          }
          onClose={() => setApercu(null)}
        />
      ) : null}
    </div>
  );
}

/* L'aperçu du document tel qu'il partira, dans le format que ce destinataire
   attend. Le modèle se règle dans Modèles usine. */
function ApercuDocument({
  cible, vente, lignes, modeles, usine, destinataire, onClose,
}: {
  cible: "client" | "usine";
  vente: Vente;
  lignes: LigneVente[];
  modeles: ModeleDocument[];
  usine: Usine | null;
  destinataire: string;
  onClose: () => void;
}) {
  const navigue = useNavigate();
  const modele =
    cible === "client"
      ? modeles.find((m) => m.cible === "client")
      : modeles.find((m) => m.cible === "usine" && m.usine_id === vente.usine_id);

  return (
    <Drawer
      titre={cible === "client" ? "Document client" : "Document usine"}
      sous={destinataire}
      onClose={onClose}
      pied={
        <button type="button" className="btn btn-sm" onClick={() => navigue("/modeles")}>
          Régler le modèle
        </button>
      }
    >
      {modele ? (
        <Document
          vente={vente}
          lignes={lignes}
          modele={modele}
          usine={usine}
          destinataire={destinataire}
        />
      ) : (
        <Empty
          titre="Aucun modèle pour ce destinataire"
          detail="Créez-le dans Modèles usine : il décide de la langue, des colonnes et des mentions."
        />
      )}
    </Drawer>
  );
}
